import { useEffect, useState } from 'react';
import {
  searchCitiesGeoapify,
  type GeoapifyCity,
} from '../services/geoapify.service';

/**
 * React hook that turns a raw query string into a debounced, abortable
 * Geoapify autocomplete result set. Designed to be the ONLY consumer
 * of {@link searchCitiesGeoapify} from React-land — components stay
 * stateless w.r.t. timers and fetch instances.
 *
 * ── How infinite loops are prevented ──────────────────────────────
 *   1. Effect dependencies are the *trimmed* query + lang only — not
 *      the results array. We never re-trigger an effect with the
 *      state it itself just produced (the classic loop trap).
 *   2. The {@code AbortController} created at the top of the effect
 *      lives only for that one invocation. The cleanup function
 *      (returned from the effect) aborts it AND clears the debounce
 *      timer, so a fast typist never has racing in-flight requests
 *      writing stale results back into state.
 *   3. State updates inside the .then() are gated on
 *      {@code !controller.signal.aborted}. A request superseded by a
 *      newer keystroke (or by component unmount) is silently dropped
 *      instead of overwriting the current state.
 *   4. The hook never calls setState during render — only inside
 *      useEffect callbacks. That removes the most common cause of
 *      "Maximum update depth exceeded" / call-stack overflows.
 *
 * ── How re-renders are minimised ──────────────────────────────────
 *   • `results` is replaced (`setResults(fresh)`) only after a real
 *     response, so identical keystrokes (rare but possible during
 *     IME / autocorrect) don't churn the array reference.
 *   • Below-threshold queries (< 2 chars) bail early via a single
 *     setState batch that resets to empty + idle.
 *   • The whole tree is one effect — no chained
 *     "fetch then setLoading then setError" cascade.
 */

export interface UseCityAutocomplete {
  results: GeoapifyCity[];
  isLoading: boolean;
  /** Truthy only on a fetch failure that wasn't an abort. */
  error: Error | null;
}

export interface UseCityAutocompleteOpts {
  /** Display language for `country` / `region`. Forwarded as-is to
   *  Geoapify; omitted from the request when undefined. */
  lang?: string;
  /** Min query length before we hit the network. Defaults to 2. */
  minChars?: number;
  /** Debounce window in ms. Defaults to 400 — comfortable for both
   *  thumb typists on mobile and fast desktop typists. */
  debounceMs?: number;
  /** ISO-3166-1 alpha-2 codes to restrict the search to. Empty / undefined
   *  searches every country Geoapify knows. Joined into a stable string
   *  for the effect dependency list so order changes don't re-fire. */
  countryCodes?: readonly string[];
}

export function useCityAutocomplete(
  query: string,
  opts: UseCityAutocompleteOpts = {},
): UseCityAutocomplete {
  const { lang, countryCodes } = opts;
  const minChars = opts.minChars ?? 2;
  const debounceMs = opts.debounceMs ?? 400;
  // Stable string key so a fresh `['DE','FR']` array on every render
  // doesn't trigger a refetch. Order-insensitive to match the API.
  const countryFilterKey = (countryCodes ?? [])
    .map((c) => c.toLowerCase())
    .sort()
    .join(',');

  const [results, setResults] = useState<GeoapifyCity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Trim outside the effect so identical trimmed values don't re-trigger
  // it (typing a trailing space after "Berlin " shouldn't refetch).
  const trimmed = query.trim();

  useEffect(() => {
    // Below the threshold → blank state, no network. Single batch.
    if (trimmed.length < minChars) {
      setResults([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    // One controller per effect invocation. Cleanup aborts it.
    const controller = new AbortController();
    // Schedule the actual fetch after the debounce window.
    const timer = window.setTimeout(() => {
      setIsLoading(true);
      setError(null);
      const codes = countryFilterKey ? countryFilterKey.split(',') : undefined;
      searchCitiesGeoapify(trimmed, {
        signal: controller.signal,
        lang,
        countryCodes: codes,
      })
        .then((fresh) => {
          // Discard stale results from requests superseded by newer
          // keystrokes / unmount — prevents the "old answer flashes
          // briefly before the right one" race.
          if (controller.signal.aborted) return;
          setResults(fresh);
          setIsLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        });
    }, debounceMs);

    return () => {
      // Order matters: clear the timer first (so the fetch never
      // fires if we're cleaning up during the debounce window), then
      // abort any in-flight request started after debounce elapsed.
      window.clearTimeout(timer);
      controller.abort();
    };
    // `trimmed` + the opts are the ONLY inputs to this effect.
    // Adding `results`/`error`/`isLoading` here would loop. Keep this
    // dependency list narrow. countryFilterKey is the stable string
    // form of countryCodes so a fresh array literal each render doesn't
    // re-fire the effect.
  }, [trimmed, lang, minChars, debounceMs, countryFilterKey]);

  return { results, isLoading, error };
}
